import { useCallback, useEffect, useState } from "react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { AppointmentLogJoin } from "@/repositories/appointments.repository";
import { fetchAppointment, fetchAppointmentLogs } from "./appointments.api";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { EMPTY_CHECKLIST, type ChecklistState } from "@/lib/appointment-checklist";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { AppointmentChecklistRepository } from "@/repositories/appointment-checklist.repository";

export const useAppointmentDetail = (id: string) => {
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<AppointmentJoin | null>(null);
  const [logs, setLogs] = useState<AppointmentLogJoin[]>([]);
  const [checklist, setChecklist] = useState<ChecklistState>(EMPTY_CHECKLIST);
  const [serviceWorkflows, setServiceWorkflows] = useState<PhotoSlot[]>([]);
  const [appointmentWorkflows, setAppointmentWorkflows] = useState<AppointmentWorkflow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [loadingSecondary, setLoadingSecondary] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Load core appointment data first (fastest)
        const [appt, logRows] = await Promise.all([fetchAppointment(id), fetchAppointmentLogs(id)]);

        if (!cancelled) {
          setAppointment(appt);
          setLogs(logRows);
          setLoading(false); // Show appointment immediately

          // Load secondary data in background
          setLoadingSecondary(true);

          const secondaryPromises = [];

          if (appt?.service?.id) {
            secondaryPromises.push(
              AppointmentPhotoRepository.slots(id).then((data) => ({
                type: "serviceWorkflows",
                data,
              }))
            );
          }

          secondaryPromises.push(
            AppointmentChecklistRepository.read(id).then((data) => ({ type: "checklist", data })),
            AppointmentPhotoRepository.read(id).then((data) => ({ type: "appointmentWorkflows", data }))
          );

          // Execute all secondary requests in parallel
          const results = await Promise.allSettled(secondaryPromises);

          if (!cancelled) {
            results.forEach((result) => {
              if (result.status === "fulfilled") {
                const { type, data } = result.value;
                switch (type) {
                  case "serviceWorkflows":
                    setServiceWorkflows(data);
                    break;
                  case "checklist":
                    setChecklist(data);
                    break;
                  case "appointmentWorkflows":
                    setAppointmentWorkflows(data);
                    break;
                }
              } else {
                console.error(`Failed to load ${result.reason?.type || "data"}:`, result.reason);
              }
            });
            setLoadingSecondary(false);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message ?? "Failed to load appointment");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const refreshLogs = useCallback(async () => {
    try {
      setLogs(await fetchAppointmentLogs(id));
    } catch (error) {
      console.error("Failed to refresh the activity:", error);
    }
  }, [id]);

  return {
    loading,
    loadingSecondary,
    appointment,
    setAppointment,
    logs,
    refreshLogs,
    serviceTasks: checklist.serviceTasks,
    serviceWorkflows,
    appointmentTasks: checklist.appointmentTasks,
    appointmentWorkflows,
    error,
    editOpen,
    setEditOpen,
  };
};
